import {DEFAULT_VISIBLE_COLUMN_KEYS, LG_COLUMNS, type ColumnKey} from '../constants/listColumns';
import {usePersistedColumnVisibility, type ColumnVisibility} from './usePersistedColumnVisibility';

export type UseColumnVisibility = ColumnVisibility<ColumnKey>;

const STORAGE_KEY = 'ticketList.visibleColumns.v1';

/**
 * When SLA became toggleable it moved out of the always-on set, so persisted
 * blobs written before that change omit `sla` — which under the new config
 * would hide the column for anyone who had ever opened the columns menu. This
 * marker gates a one-shot rewrite that adds `sla` back to those blobs; once
 * it runs, later toggles of SLA are honoured normally.
 */
const SLA_MIGRATION_MARKER_KEY = 'ticketList.visibleColumns.slaMigrated';

function migrateLegacyBlobOnce(): void {
	if (typeof window === 'undefined') return;
	try {
		if (window.localStorage.getItem(SLA_MIGRATION_MARKER_KEY) === '1') return;
		const raw = window.localStorage.getItem(STORAGE_KEY);
		if (raw) {
			const parsed = JSON.parse(raw);
			if (Array.isArray(parsed) && !parsed.includes('sla')) {
				parsed.push('sla');
				window.localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
			}
		}
		window.localStorage.setItem(SLA_MIGRATION_MARKER_KEY, '1');
	} catch {
		/* corrupt blob — the hook will fall back to defaults anyway */
	}
}

/** Column visibility for the ticket list table. */
export function useColumnVisibility(): UseColumnVisibility {
	migrateLegacyBlobOnce();
	return usePersistedColumnVisibility<ColumnKey>({
		storageKey: STORAGE_KEY,
		columns: LG_COLUMNS,
		defaultVisibleKeys: DEFAULT_VISIBLE_COLUMN_KEYS,
	});
}
